import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { subscribeToNewsletter } from '@/services/newsletterService';
import { useToast } from '@/hooks/use-toast';

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    try {
      const result = await subscribeToNewsletter({ email });
      if (result) {
        setIsSuccess(true);
        setEmail('');
        toast({
          title: t('footer.thanks'),
          description: t('footer.subscriptionSuccess'),
        });
      }
    } catch (error) {
      toast({
        title: t('newsletter.error.title'),
        description: t('newsletter.error.description'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="relative text-white pt-16 pb-8 font-sans overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://res.cloudinary.com/dsturnqnq/image/upload/v1748776873/MKxHammamet_footer_1_hauvtl.png')`,
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/70" aria-hidden />

      <div className="relative z-10">
        <div className="flex flex-col items-center mb-12">
          <span className="text-4xl font-sans font-semibold tracking-widest mb-2">Meubles Karim</span>
        </div>

        <div className="container-custom grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <h4 className="text-xl font-sans font-semibold mb-6 tracking-wide">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3 text-base">
              <li>
                <Link to="/" className="hover:text-furniture-gold transition-colors">{t('footer.home')}</Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-furniture-gold transition-colors">{t('footer.products')}</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-furniture-gold transition-colors">{t('footer.about')}</Link>
              </li>
              <li>
                <Link to="/events" className="hover:text-furniture-gold transition-colors">{t('footer.events')}</Link>
              </li>
              <li>
                <Link to="/careers" className="hover:text-furniture-gold transition-colors">{t('footer.careers')}</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-furniture-gold transition-colors">{t('footer.contact')}</Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-sans font-semibold mb-6 tracking-wide">{t('footer.contactUs')}</h4>
            <ul className="space-y-5 text-base">
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-furniture-gold" />
                <span> (+216) 72 260 360 </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-furniture-gold" />
                <span>contactmkarim@gmail.com</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-furniture-gold" />
                <span>Route Hammamet Nord vers Nabeul,<br />Hammamet, Tunisia, 8050</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-sans font-semibold mb-6 tracking-wide">{t('footer.subscribeToNewsletter')}</h4>
            {isSuccess ? (
              <div className="text-center py-8 px-6 rounded-md border border-furniture-gold/40 animate-fade-in">
                <div className="flex justify-center mb-2"><Mail className="h-8 w-8 text-furniture-gold" /></div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('footer.thanks')}</h3>
                <p className="text-white/80">{t('footer.subscriptionSuccess')}</p>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
                <Label htmlFor="newsletter-email" className="text-base text-white">{t('footer.email')}</Label>
                <Input
                  id="newsletter-email"
                  type="email"
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="bg-transparent border-furniture-gold text-white placeholder:text-white/50 focus-visible:ring-furniture-gold"
                  autoComplete="off"
                  required
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  variant="outline"
                  className="border-furniture-gold text-furniture-gold bg-transparent hover:bg-furniture-gold hover:text-black"
                  disabled={isLoading}
                >
                  {isLoading ? '...' : t('footer.subscribe')}
                </Button>
              </form>
            )}
            <div className="flex gap-6 mt-8">
              <a href="https://facebook.com/MeublesKarim" className="hover:opacity-80 transition-opacity" aria-label="Facebook">
                <Facebook size={24} className="text-furniture-gold" />
              </a>
              <a href="https://www.instagram.com/meubles.karim/" className="hover:opacity-80 transition-opacity" aria-label="Instagram">
                <Instagram size={24} className="text-furniture-gold" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 pt-6 mt-8 text-center text-sm text-white/60 flex flex-col md:flex-row justify-between items-center gap-2 px-4 container-custom">
          <span>&copy; {currentYear} Meubles Karim. Tous droits réservés.</span>
          <div className="flex items-center gap-2 text-xs">
            <span>Powered by</span>
            <img
              src="/OrcaLogo.png"
              alt="Orca Consulting"
              className="h-12 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
